/**
 * Widget Container Component
 * Wraps widgets with header, controls, styling, and filter support
 * Includes export functionality for JSON, XML, and XLSX
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import {
  MoreVertical,
  RefreshCw,
  Maximize2,
  X,
  Settings,
  Download,
  FileJson,
  FileSpreadsheet,
  FileCode,
  ChevronRight,
  Filter,
  Pencil,
  ChevronDown,
} from 'lucide-react';
import { Widget, WidgetData } from '../../types';
import { WidgetFilterConfig, createDefaultFilterConfig } from '../../types/filters';
import { WidgetRenderer } from './WidgetRenderer';
import { FilterPanel } from './FilterPanel';
import { FilterChips, FilterIndicator } from './FilterChips';
import { applyFilters } from '../../utils/filterData';
import { exportData, ExportFormat } from '../../utils/exportData';
import clsx from 'clsx';

interface WidgetContainerProps {
  widget: Widget;
  data?: WidgetData;
  loading?: boolean;
  error?: string;
  onRefresh?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onFilterChange?: (widgetId: string, filterConfig: WidgetFilterConfig) => void;
  isEditMode?: boolean;
}

export function WidgetContainer({
  widget,
  data,
  loading,
  error,
  onRefresh,
  onEdit,
  onDelete,
  onFilterChange,
  isEditMode,
}: WidgetContainerProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Initialize filter config from widget.config or create default
  const [filterConfig, setFilterConfig] = useState<WidgetFilterConfig>(() => {
    if (widget.config?.filters) {
      return widget.config.filters as WidgetFilterConfig;
    }
    return createDefaultFilterConfig();
  });

  // Apply filters to data
  const filteredData = useMemo<WidgetData | undefined>(() => {
    if (!data) return data;
    if (!data.rows || data.rows.length === 0) return data;

    const filteredRows = applyFilters(data.rows, filterConfig);

    return {
      ...data,
      rows: filteredRows,
      totalCount: data.totalCount, // Keep original count
    };
  }, [data, filterConfig]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return filterConfig.groups.reduce(
      (sum, group) => sum + group.conditions.length,
      0
    );
  }, [filterConfig]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
        setShowExportMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
    setShowExportMenu(false);
  };

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onRefresh?.();
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onEdit?.();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onDelete?.();
  };

  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    setIsExpanded(true);
  };

  const handleExport = (format: ExportFormat) => {
    // Export filtered data
    const rowsToExport = filteredData?.rows || data?.rows;
    if (!rowsToExport || rowsToExport.length === 0) {
      alert('No data available for export');
      return;
    }

    setIsExporting(true);
    setShowMenu(false);
    setShowExportMenu(false);

    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const filename = `${widget.name}_${timestamp}`;

      exportData({
        filename,
        data: rowsToExport,
        format,
      });
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handleFilterChange = (newConfig: WidgetFilterConfig) => {
    setFilterConfig(newConfig);
    // Notify parent to persist filter config
    onFilterChange?.(widget.id, newConfig);
  };

  const handleRemoveFilterCondition = (groupId: string, conditionId: string) => {
    const newConfig: WidgetFilterConfig = {
      ...filterConfig,
      groups: filterConfig.groups
        .map((group) =>
          group.id === groupId
            ? {
                ...group,
                conditions: group.conditions.filter((c) => c.id !== conditionId),
              }
            : group
        )
        .filter((group) => group.conditions.length > 0),
    };
    newConfig.enabled = newConfig.groups.length > 0;
    handleFilterChange(newConfig);
  };

  const handleClearAllFilters = () => {
    handleFilterChange({
      enabled: false,
      groups: [],
    });
  };

  const hasData = data?.rows && data.rows.length > 0;
  const filteredCount = filteredData?.rows?.length ?? 0;
  const totalCount = data?.rows?.length ?? 0;

  // Expanded modal view
  if (isExpanded) {
    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsExpanded(false)}
        />
        {/* Modal */}
        <div className="fixed inset-8 bg-white rounded-lg shadow-2xl z-50 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <h3 className="font-medium text-gray-900">{widget.name}</h3>
              {activeFilterCount > 0 && (
                <FilterIndicator
                  filterConfig={filterConfig}
                  onClick={() => setShowFilterPanel(true)}
                />
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Filter Button */}
              <button
                onClick={() => setShowFilterPanel(true)}
                className={clsx(
                  "flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors",
                  activeFilterCount > 0
                    ? "text-[#45bdd6] bg-[#45bdd6]/10 hover:bg-[#45bdd6]/20"
                    : "text-gray-600 hover:bg-gray-100"
                )}
                title="Filter"
              >
                <Filter className="w-4 h-4" />
                Filter
              </button>

              {/* Export buttons */}
              {hasData && (
                <div className="flex items-center gap-1 ml-2 border-l border-gray-200 pl-2">
                  <button
                    onClick={() => handleExport('json')}
                    className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Export as JSON (includes nested Ref data)"
                  >
                    <FileJson className="w-4 h-4" />
                    JSON
                  </button>
                  <button
                    onClick={() => handleExport('xml')}
                    className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Export as XML (includes nested Ref data)"
                  >
                    <FileCode className="w-4 h-4" />
                    XML
                  </button>
                  <button
                    onClick={() => handleExport('xlsx')}
                    className="flex items-center gap-1.5 px-2 py-1 text-xs text-green-600 hover:bg-green-50 rounded transition-colors"
                    title="Export as Excel (separate sheets for Ref data)"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Excel
                  </button>
                </div>
              )}
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 hover:bg-gray-100 rounded ml-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Filter Chips Bar */}
          {activeFilterCount > 0 && (
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
              <FilterChips
                filterConfig={filterConfig}
                onRemoveCondition={handleRemoveFilterCondition}
                onClearAll={handleClearAllFilters}
                onOpenFilter={() => setShowFilterPanel(true)}
              />
            </div>
          )}

          <div className="flex-1 overflow-auto p-4">
            <WidgetRenderer
              widget={widget}
              data={filteredData}
              loading={loading}
              error={error}
            />
          </div>

          {/* Footer with record count */}
          {hasData && (
            <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
              {activeFilterCount > 0 ? (
                <span>
                  <span className="font-medium text-[#45bdd6]">{filteredCount}</span>
                  {' von '}
                  {totalCount} records (filtered)
                </span>
              ) : (
                <span>{totalCount} records</span>
              )}
            </div>
          )}
        </div>

        {/* Filter Panel Modal */}
        <FilterPanel
          isOpen={showFilterPanel}
          onClose={() => setShowFilterPanel(false)}
          filterConfig={filterConfig}
          onFilterChange={handleFilterChange}
          data={data?.rows || []}
        />
      </>
    );
  }

  return (
    <div
      className={clsx(
        'bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-full',
        isEditMode && 'cursor-move'
      )}
    >
      {/* Widget Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-medium text-gray-700 truncate">
            {widget.name}
          </h3>
          {activeFilterCount > 0 && (
            <FilterIndicator
              filterConfig={filterConfig}
              onClick={() => setShowFilterPanel(true)}
            />
          )}
        </div>
        <div className="flex items-center gap-1">
          {(loading || isExporting) && (
            <RefreshCw className="w-4 h-4 text-[#45bdd6] animate-spin" />
          )}

          {/* Filter Button */}
          {hasData && (
            <button
              onClick={() => setShowFilterPanel(true)}
              className={clsx(
                "p-1 rounded transition-colors",
                activeFilterCount > 0
                  ? "text-[#45bdd6] hover:bg-[#45bdd6]/10"
                  : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              )}
              title={activeFilterCount > 0 ? `${activeFilterCount} active filters` : 'Add filter'}
            >
              <Filter className="w-4 h-4" />
            </button>
          )}

          {/* Quick Access: Refresh Button */}
          {!loading && (
            <button
              onClick={handleRefresh}
              className="p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded transition-colors"
              title="Refresh data"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}

          {/* Quick Access: Export Dropdown */}
          {hasData && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowExportMenu(!showExportMenu);
                  setShowMenu(false);
                }}
                className="p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded transition-colors flex items-center"
                title="Export data"
              >
                <Download className="w-4 h-4" />
              </button>
              {showExportMenu && (
                <div className="absolute right-0 mt-1 w-36 bg-white rounded-md shadow-lg border border-gray-200 z-30">
                  <button
                    onClick={() => handleExport('json')}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <FileJson className="w-4 h-4 text-yellow-600" />
                    JSON
                  </button>
                  <button
                    onClick={() => handleExport('xml')}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <FileCode className="w-4 h-4 text-orange-600" />
                    XML
                  </button>
                  <button
                    onClick={() => handleExport('xlsx')}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-green-600" />
                    Excel
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Quick Access: Edit Button */}
          <button
            onClick={handleEdit}
            className="p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded transition-colors"
            title="Edit widget"
          >
            <Pencil className="w-4 h-4" />
          </button>

          <div className="relative" ref={menuRef}>
            <button
              onClick={handleMenuToggle}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-20">
                <button
                  onClick={handleRefresh}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>

                {/* Filter Button in Menu */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    setShowFilterPanel(true);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Filter className="w-4 h-4" />
                  Filter
                  {activeFilterCount > 0 && (
                    <span className="ml-auto text-xs bg-[#45bdd6]/10 text-[#45bdd6] px-1.5 py-0.5 rounded-full">
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={handleExpand}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Maximize2 className="w-4 h-4" />
                  Expand
                </button>

                {/* Export Submenu */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowExportMenu(!showExportMenu);
                    }}
                    disabled={!hasData}
                    className={clsx(
                      "flex items-center justify-between w-full px-3 py-2 text-sm",
                      hasData
                        ? "text-gray-700 hover:bg-gray-50"
                        : "text-gray-400 cursor-not-allowed"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      Export
                    </div>
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  {showExportMenu && hasData && (
                    <div className="absolute left-full top-0 ml-1 w-40 bg-white rounded-md shadow-lg border border-gray-200">
                      <button
                        onClick={() => handleExport('json')}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <FileJson className="w-4 h-4 text-yellow-600" />
                        JSON
                      </button>
                      <button
                        onClick={() => handleExport('xml')}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <FileCode className="w-4 h-4 text-orange-600" />
                        XML
                      </button>
                      <button
                        onClick={() => handleExport('xlsx')}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <FileSpreadsheet className="w-4 h-4 text-green-600" />
                        Excel (XLSX)
                      </button>
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-100 my-1" />

                <button
                  onClick={handleEdit}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Settings className="w-4 h-4" />
                  Edit
                </button>

                {isEditMode && (
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Chips Bar (when filters are active) */}
      {activeFilterCount > 0 && (
        <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">
              <span className="font-medium text-[#45bdd6]">{filteredCount}</span>
              {' / '}
              {totalCount}
            </span>
            <button
              onClick={handleClearAllFilters}
              className="text-gray-400 hover:text-red-500 transition-colors"
              title="Clear filters"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Widget Content */}
      <div className="flex-1 overflow-auto min-h-0">
        <WidgetRenderer
          widget={widget}
          data={filteredData}
          loading={loading}
          error={error}
        />
      </div>

      {/* Filter Panel Modal */}
      <FilterPanel
        isOpen={showFilterPanel}
        onClose={() => setShowFilterPanel(false)}
        filterConfig={filterConfig}
        onFilterChange={handleFilterChange}
        data={data?.rows || []}
      />
    </div>
  );
}
